import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Space, Tooltip } from 'antd';
import { SettingOutlined, FileImageOutlined, FileAddOutlined } from '@ant-design/icons';
import { useSize } from 'ahooks';
import SegmentsResult from 'containers/Tools/CustomResults/SegmentsResult';
import ResultsConfig from 'containers/Tools/CustomResults/ResultsConfig';
import DrawerForm from 'components/DrawerForm';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import ResultFilter from 'components/Dashboard/ResultFilter';
import { toPng } from 'html-to-image';
import _ from 'lodash';
import {
  combineQueries,
} from 'shared/utils/queryBuilder';

const saveAsImageFilter = (node) => {
  const exclusionClasses = ['ant-btn', 'dashboard__content__footer'];
  return !exclusionClasses.some((classname) => node.classList?.contains(classname));
};

const ResultContent = ({
  content,
  onUpdateFilter,
  globalFilter,
  dashboardFilter,
  onSizeChange,
  onUpdateContent,
  canOverrideFilter,
  onCopyToDashboard,
  customerId,
  dataSourceIds,
  websites,
  viewDate,
  skipCurrencyQuery,
}) => {
  const cardRef = useRef();
  const contentRef = useRef();
  const size = useSize(cardRef);
  const [resultsConfig, setResultsConfig] = useState(null);
  const activeFilter = content.filter || globalFilter;

  useEffect(() => {
    onSizeChange?.(content.id, size);
  }, [size, content.id, onSizeChange]);

  const onSaveAsImage = useCallback(() => {
    toPng(contentRef.current, { cacheBust: true, filter: saveAsImageFilter }).then(dataUrl => {
      const link = document.createElement('a');
      link.download = 'extellio-result.png';
      link.href = dataUrl;
      link.click();
    });
  }, [contentRef]);

  const onResultsConfigSubmit = useCallback(async (values) => {
    if (onUpdateContent) {
      await onUpdateContent(content.id, { resultsConfig: values });
    }
    setResultsConfig(null);
  }, [onUpdateContent, content.id]);

  const onQueryFilterSave = useCallback(async (filter) => {
    if (onUpdateContent) {
      await onUpdateContent(content.id, {
        resultsConfig: {
          ...(_.omit(content.resultsConfig, ['id']) || {}),
          filter,
        },
      });
    }
  }, [onUpdateContent, content.id, content.resultsConfig]);

  const getContentDataForCopy = useCallback(() => {
    const data = _.omit(content, ['id', '__typename']);
    if (!dashboardFilter) {
      return data;
    }
    return {
      ...data,
      resultsConfig: {
        ...(data.resultsConfig || {}),
        filter: combineQueries(data.resultsConfig?.filter, dashboardFilter),
      },
    };
  }, [content, dashboardFilter]);

  return (
    <>
      <Card
        className="dashboard__content"
        ref={cardRef}
        title={content.title}
        extra={(
          <Space.Compact>
            <ResultFilter
              queryFilter={content.resultsConfig?.filter}
              scopeFilter={content.filter}
              runtimeQueryFilter={dashboardFilter}
              globalFilter={globalFilter}
              customerId={customerId}
              contentId={content.id}
              websites={websites}
              canEditQuery={!!onUpdateContent}
              canEditScope={canOverrideFilter}
              onQueryFilterSave={onQueryFilterSave}
              onScopeFilterChange={onUpdateFilter}
              onRemoveScopeFilter={onUpdateFilter}
            />
            {onUpdateContent && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => {
                  setResultsConfig(content.resultsConfig || {});
                }}
              />
            )}
            <CopyToClipboardButton textToCopy={JSON.stringify(getContentDataForCopy())} />
            {onCopyToDashboard && <Tooltip title="Copy to report or dashboard"><Button onClick={() => onCopyToDashboard(getContentDataForCopy())} icon={<FileAddOutlined />} /></Tooltip>}
            {contentRef.current && <Tooltip title="Save as image"><Button onClick={onSaveAsImage} icon={<FileImageOutlined />} /></Tooltip>}
          </Space.Compact>
        )}
      >
        <SegmentsResult
          richText={content.richText}
          resultsConfig={content.resultsConfig}
          filter={activeFilter}
          contextFilter={dashboardFilter}
          width={size?.width}
          contentRef={contentRef}
          customerId={customerId}
          dataSourceIds={dataSourceIds}
          websites={websites}
          viewDate={viewDate}
          skipCurrencyQuery={skipCurrencyQuery}
        />
      </Card>
      <DrawerForm
        title="Result settings"
        open={!!resultsConfig}
        onCancel={() => setResultsConfig(null)}
        onSubmit={onResultsConfigSubmit}
        initialValues={resultsConfig}
      >
        <ResultsConfig customerId={customerId} />
      </DrawerForm>
    </>
  );
};

export default ResultContent;
